import { TableMapping } from "@made-people/centra-checkout-api";
import { TranslateFunction } from "@made-people/grease-translations";
import {
  isWeeksStatus,
  parseWarehouseStatus,
} from "../../../services/centra/mappers/utils/parse-warehouse-status";

type StockStatusResult = {
  deliveryStatus: string;
  isImmediateDelivery: boolean;
};

export type AllocationRuleInfo = {
  name: string | null;
  hasLitWarehouse: boolean;
};

function returnStatus(
  translate: TranslateFunction,
  status: string,
  warehouse: string,
  allocationRuleName: string | null,
  isImmediateDelivery: boolean,
): StockStatusResult {
  const deliveryStatus = allocationRuleName
    ? translate(`Delivery_${status}_${allocationRuleName}_${warehouse}`)
    : translate(`Delivery_${status}`);
  return {
    deliveryStatus,
    isImmediateDelivery,
  };
}

export function getStockStatus(
  productBase: {
    discontinued?: boolean;
    litWarehouseStatus: string | null;
    warehouse100Status?: string | null;
    tableMapping: TableMapping;
  },
  sizeLabel: string,
  translate: TranslateFunction,
  allocationRule: AllocationRuleInfo,
  stockQuantity?: number,
): StockStatusResult {
  const litStatus = allocationRule.hasLitWarehouse
    ? parseWarehouseStatus(
        productBase.litWarehouseStatus,
        sizeLabel,
        productBase.tableMapping,
      )
    : null;

  const status100 = parseWarehouseStatus(
    productBase.warehouse100Status ?? null,
    sizeLabel,
    productBase.tableMapping,
  );

  // If the litStatus is OMG it means that the item is physically in warehouse, so we consider it in stock
  if (litStatus === "OMG") {
    return returnStatus(
      translate,
      "In_Stock",
      "LIT",
      allocationRule.name,
      true,
    );
  }

  // If the status100 is OMG it means that the item is physically in warehouse, so we consider it in stock
  if (status100 === "OMG") {
    return returnStatus(
      translate,
      "In_Stock",
      "100",
      allocationRule.name,
      true,
    );
  }

  // Stock quantity is undefined for cart items (but item is in cart, so it has virtual stock)
  const hasVirtualStock = stockQuantity === undefined || stockQuantity > 0;

  if (hasVirtualStock) {
    // If the litStatus is a weeks status it means that the item is not in stock but restocking and it will be delivered in x weeks
    if (isWeeksStatus(litStatus)) {
      return returnStatus(
        translate,
        "In_X_Weeks",
        "LIT",
        allocationRule.name,
        false,
      );
    }

    // If the status100 is a weeks status it means that the item is not in stock but restocking and it will be delivered in x weeks
    if (isWeeksStatus(status100)) {
      return returnStatus(
        translate,
        "In_X_Weeks",
        "100",
        allocationRule.name,
        false,
      );
    }

    // Based on logical flow this should not happen as it means that the item is not in stock and not restocking and it has virtual stock,
    // but added as a safeguard if data is not following the decided logical flow
    return returnStatus(
      translate,
      "Virtual_Stock",
      "Virtual",
      allocationRule.name,
      true,
    );
  }

  // If the product is discontinued it means that the item is not in stock, so we consider it out of stock
  if (productBase.discontinued) {
    return {
      deliveryStatus: translate("Delivery_Out_Of_Stock"),
      isImmediateDelivery: false,
    };
  }

  // If the product is not discontinued it means that the item is temporarily out of stock, so we consider it temporarily out of stock
  return {
    deliveryStatus: translate("Delivery_Temporarily_Out_Of_Stock"),
    isImmediateDelivery: false,
  };
}
